import cronParser from "cron-parser";
import { getCronDisplayStatus } from "./cronDisplayStatus";
import { getCronCategory, getCronDisplayName } from "./cronCatalog";

export const DISPLAY_TIME_ZONE = "America/Denver";

export const CRON_STATUS_LABELS = {
    COMPLETED: "Completed",
    WARNING: "Warning",
    ERROR: "Error",
    IN_PROGRESS: "Running",
    PAUSED: "Paused",
    NEVER: "Never run",
    UNKNOWN: "No status",
};

const toTimestamp = (value) => {
    if (value == null || value === "") return null;
    if (typeof value === "number") return value;
    const text = String(value).trim();
    const hasZone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(text);
    const parsed = Date.parse(hasZone ? text : `${text.replace(" ", "T")}Z`);
    return Number.isNaN(parsed) ? null : parsed;
};

export const parseCronTimestamp = toTimestamp;

export const validateCronExpression = (expression) => {
    const value = String(expression || "").trim();
    if (!value) return "Enter a schedule.";
    if (value.split(/\s+/).length !== 5) return "Use 5 fields: minute hour day-of-month month day-of-week.";
    try {
        cronParser.parseExpression(value);
        return null;
    } catch (error) {
        return `Invalid schedule: ${String(error?.message || error).replace(/^Constraint error, /, "")}.`;
    }
};

export const getCronRuns = (expression, timeZone, { from = Date.now(), count = 1, direction = "next" } = {}) => {
    if (validateCronExpression(expression)) return [];
    try {
        const interval = cronParser.parseExpression(String(expression).trim(), {
            tz: timeZone || DISPLAY_TIME_ZONE,
            currentDate: new Date(from),
        });
        return Array.from({ length: count }, () => (
            direction === "prev" ? interval.prev() : interval.next()
        ).getTime());
    } catch {
        return [];
    }
};

export const timeZoneLabel = (timeZone) => {
    if (timeZone === "UTC") return "UTC";
    if (timeZone === "America/Los_Angeles") return "PT";
    if (!timeZone || timeZone === "America/Denver") return "MT";
    return timeZone;
};

const ordinal = (n) => n + (n % 100 >= 11 && n % 100 <= 13 ? "th" : ({ 1: "st", 2: "nd", 3: "rd" }[n % 10] || "th"));
const clock = (hour, minute) => `${hour % 12 || 12}:${String(minute).padStart(2, "0")} ${hour < 12 ? "AM" : "PM"}`;
const hourLabel = (hour) => `${hour % 12 || 12} ${hour < 12 ? "AM" : "PM"}`;
const WEEKDAYS = ["Sundays", "Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays"];
const joinList = (items) => (items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items[0]);

export const describeCronSchedule = (expression, timeZone) => {
    if (validateCronExpression(expression)) return null;
    const [minute, hour, dom, month, dow] = String(expression).trim().split(/\s+/);
    if (month !== "*") return null;
    const zone = ` ${timeZoneLabel(timeZone)}`;
    let day;
    if (dom === "*" && dow === "*") day = "Daily";
    else if (dom === "*" && dow === "1-5") day = "Weekdays";
    else if (dom === "*" && /^\d$/.test(dow)) day = WEEKDAYS[Number(dow) % 7];
    else if (/^\*\/\d+$/.test(dom) && dow === "*") day = `Every ${dom.slice(2)} days`;
    else if (/^\d+$/.test(dom) && dow === "*") day = `Monthly on the ${ordinal(Number(dom))}`;
    else return null;

    const daily = day === "Daily";
    if (/^\*\/\d+$/.test(minute) && hour === "*") {
        return daily ? `Every ${minute.slice(2)} minutes` : `${day}, every ${minute.slice(2)} minutes`;
    }
    if (/^\d+$/.test(minute) && hour === "*") {
        const at = `:${minute.padStart(2, "0")}`;
        return daily ? `Hourly at ${at}` : `${day}, hourly at ${at}`;
    }
    if (/^\d+$/.test(minute) && /^\*\/\d+$/.test(hour)) {
        const every = daily ? `Every ${hour.slice(2)} hours` : `${day}, every ${hour.slice(2)} hours`;
        return Number(minute) ? `${every} at :${minute.padStart(2, "0")}` : every;
    }
    if (/^\d+$/.test(minute) && /^\d+-\d+$/.test(hour)) {
        const [start, end] = hour.split("-").map(Number);
        return `${day}, hourly ${hourLabel(start)}–${hourLabel(end)}${zone}`;
    }
    if (/^\d+$/.test(minute) && /^\d+(,\d+)*$/.test(hour)) {
        const times = hour.split(",").map((h) => clock(Number(h), Number(minute)));
        return `${day} at ${joinList(times)}${zone}`;
    }
    return null;
};

const formatter = (options) => new Intl.DateTimeFormat("en-US", { timeZone: DISPLAY_TIME_ZONE, ...options });
const dateFormat = formatter({ month: "2-digit", day: "2-digit", year: "numeric" });
const timeFormat = formatter({ hour: "numeric", minute: "2-digit" });
const zoneFormat = formatter({ timeZoneName: "short" });
const longFormat = formatter({ weekday: "long", month: "long", day: "numeric", year: "numeric" });

const partValue = (parts, type) => parts.find((part) => part.type === type)?.value;

export const formatCronTime = (ts) => timeFormat.format(ts);
export const formatCronCompact = (ts) => `${dateFormat.format(ts)} · ${timeFormat.format(ts)}`;
export const formatCronTimeWithZone = (ts) => `${timeFormat.format(ts)} ${partValue(zoneFormat.formatToParts(ts), "timeZoneName")}`;
export const formatCronLongDate = (ts) => {
    const parts = longFormat.formatToParts(ts);
    return `${partValue(parts, "weekday")}, ${partValue(parts, "month")} ${ordinal(Number(partValue(parts, "day")))}, ${partValue(parts, "year")}`;
};

const calendarDay = (ts) => {
    const [month, day, year] = dateFormat.format(ts).split("/").map(Number);
    return Date.UTC(year, month - 1, day);
};

export const formatCronRelative = (ts, now = Date.now()) => {
    const seconds = Math.round((now - ts) / 1000);
    const future = seconds < 0;
    const abs = Math.abs(seconds);
    if (abs < 45) return future ? "in a moment" : "just now";
    const minutes = Math.round(abs / 60);
    if (minutes < 60) return future ? `in ${minutes} min` : `${minutes} min ago`;
    const hours = Math.round(abs / 3600);
    if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;
    const days = Math.round(Math.abs(calendarDay(now) - calendarDay(ts)) / 86400000);
    if (days <= 1) return future ? "tomorrow" : "yesterday";
    if (days < 30) return future ? `in ${days} days` : `${days} days ago`;
    if (days < 365) return future ? `in ${Math.round(days / 30)} mo` : `${Math.round(days / 30)} mo ago`;
    return future ? `in ${Math.round(days / 365)} yr` : `${Math.round(days / 365)} yr ago`;
};

// Inferred from the latest recorded run vs. the schedule; Eadin keeps no run ledger.
export const getMissedRunAt = ({ cronInterval, timeZone, lastRunAt, now = Date.now() }) => {
    if (!cronInterval || lastRunAt == null) return null;
    const [latest, previous] = getCronRuns(cronInterval, timeZone, { from: now, count: 2, direction: "prev" });
    if (!latest) return null;
    const gap = previous ? latest - previous : 3600e3;
    const grace = Math.min(3 * 3600e3, Math.max(15 * 60e3, gap));
    return lastRunAt < latest - 60e3 && now - latest > grace ? latest : null;
};

const STATUS_ORDER = ["IN_PROGRESS", "ERROR", "WARNING", "NEVER", "UNKNOWN", "COMPLETED", "PAUSED"];

export const deriveCronRow = (cron, { kind = "job", now = Date.now(), runRequest = null } = {}) => {
    const serverStatus = getCronDisplayStatus(cron);
    const lastRunAt = toTimestamp(cron?.lastUpdateRaw);
    const timeZone = cron?.timeZone || DISPLAY_TIME_ZONE;
    const cronInterval = String(cron?.cronInterval || "").trim();
    const isPaused = !!cron?.isPaused;
    const isRunning = serverStatus === "IN_PROGRESS" || (!!runRequest && !isPaused);
    const settledStatus = isPaused ? "PAUSED" : serverStatus && serverStatus !== "IN_PROGRESS"
        ? serverStatus
        : lastRunAt ? "UNKNOWN" : "NEVER";
    const status = isRunning ? "IN_PROGRESS" : settledStatus;
    const missedAt = isPaused || isRunning ? null : getMissedRunAt({ cronInterval, timeZone, lastRunAt, now });
    const { name, comingSoon } = getCronDisplayName(cron);

    let attention = !isPaused && (
        ["WARNING", "ERROR"].includes(settledStatus)
        || (settledStatus === "NEVER" && !!cronInterval)
        || !!missedAt
    );
    let rank = settledStatus === "ERROR" ? 0 : settledStatus === "WARNING" ? 1 : missedAt ? 2 : settledStatus === "NEVER" ? 3 : 9;
    if (isRunning && runRequest) {
        attention = !!runRequest.attention;
        rank = runRequest.rank ?? rank;
    } else if (isRunning) {
        attention = false;
        rank = 9;
    }

    const schedule = cronInterval ? describeCronSchedule(cronInterval, timeZone) : null;
    const nextRunAt = isPaused || !cronInterval ? null : getCronRuns(cronInterval, timeZone, { from: now })[0] ?? null;
    const category = getCronCategory(cron?.type);

    return {
        key: `${kind}:${cron?.type}`,
        cron,
        kind,
        type: cron?.type,
        name,
        comingSoon,
        category,
        status,
        statusLabel: CRON_STATUS_LABELS[status],
        settledStatus,
        isPaused,
        isRunning,
        runRequestedAt: runRequest?.at ?? null,
        cronInterval,
        schedule,
        timeZone,
        lastRunAt,
        nextRunAt,
        missedAt,
        message: cron?.processError || null,
        attention,
        rank,
        searchText: [name, cron?.type, cronInterval, schedule, cron?.processError, cron?.description, category, CRON_STATUS_LABELS[status]]
            .filter(Boolean).join(" ").toLowerCase(),
    };
};

export const matchesCronStatus = (row, status) => {
    if (!status || status === "all") return true;
    if (status === "attention") return row.attention;
    if (status === "missed") return !!row.missedAt;
    return row.status === status;
};

export const filterCronRows = (rows, { query = "", status = "all", category = null, kind = "all" } = {}) => {
    const q = query.trim().toLowerCase();
    return rows.filter((row) => (kind === "all" || row.kind === kind)
        && (!category || row.category === category)
        && matchesCronStatus(row, status)
        && (!q || row.searchText.includes(q)));
};

const SORT_KEYS = {
    name: (row) => row.name.toLowerCase(),
    status: (row) => row.rank * 10 + STATUS_ORDER.indexOf(row.status),
    last: (row) => row.lastRunAt ?? 0,
    next: (row) => row.nextRunAt ?? Infinity,
};

export const sortCronRows = (rows, { column = null, direction = "asc" } = {}) => {
    const key = SORT_KEYS[column];
    const dir = direction === "desc" ? -1 : 1;
    return [...rows].sort((a, b) => {
        if (key) {
            const x = key(a);
            const y = key(b);
            return ((x < y ? -1 : x > y ? 1 : 0) * dir) || a.name.localeCompare(b.name);
        }
        if (a.attention !== b.attention) return a.attention ? -1 : 1;
        return (a.attention ? a.rank - b.rank : 0) || a.name.localeCompare(b.name);
    });
};
